/**
 * manage_team_controller.js — Team management page behaviour (Stimulus).
 *
 * Ports static/js/manage_team.js (attachOnce('manageTeamListeners') + turbo:load)
 * to an element-scoped controller. The original attachOnce registered a
 * turbo:load listener once per page session that, gated on #confirmModal
 * existing, (re)bound change handlers to the member-filter radios and the
 * role-select dropdowns on every navigation. connect() runs on initial load,
 * on every Turbo navigation onto this page, AND on every reload of the
 * #team-live-region Turbo Frame (the controller hosts on a div INSIDE that
 * frame, so a frame swap destroys+recreates its element and re-fires connect()).
 * We do that same gated rebind there. The element handlers are not
 * document-delegated and live on freshly rendered DOM, so there is nothing to
 * remove on disconnect().
 *
 * The action functions (suspendMember, removeMember, etc.) stay module-level and
 * exported on window: they are invoked by the template dispatcher via
 * data-action="call-fn" (global-actions.js), independent of this controller.
 *
 * data-controller="manage-team" hosts on a div inside the #team-live-region
 * Turbo Frame in templates/team/manage_team.html.
 *
 * Source (to delete): static/js/manage_team.js
 * Template:           templates/team/manage_team.html
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            var confirmModalEl = document.getElementById('confirmModal');
            if (!confirmModalEl) return;

            // Filter functionality
            document.querySelectorAll('input[name="memberFilter"]').forEach(function (radio) {
                radio.addEventListener('change', function () {
                    var filterValue = this.id.replace('filter', '').toLowerCase();
                    document.querySelectorAll('.member-row').forEach(function (row) {
                        var status = row.getAttribute('data-status');
                        if (filterValue === 'all' || status === filterValue) {
                            row.classList.remove('filtered-out');
                        } else {
                            row.classList.add('filtered-out');
                        }
                    });
                });
            });

            // Role change functionality
            document.querySelectorAll('.role-select').forEach(function (select) {
                select.addEventListener('change', function () {
                    var memberId = this.getAttribute('data-member-id');
                    var currentRole = this.getAttribute('data-current-role');
                    var newRole = this.value;
                    var selectEl = this;

                    if (newRole !== currentRole) {
                        showConfirmModal(
                            'Change Member Role',
                            'Are you sure you want to change this member\'s role to ' + newRole + '?',
                            'Change Role',
                            function () { changeMemberRole(memberId, newRole, selectEl); }
                        );
                    }
                });
            });
        }
}

function showSuccessToast(message) {
    var toastEl = document.getElementById('successToast');
    document.getElementById('successToastBody').textContent = message;
    bootstrap.Toast.getOrCreateInstance(toastEl, { delay: 3000 }).show();
}

function showConfirmModal(title, body, actionText, actionCallback) {
    document.getElementById('confirmModalTitle').textContent = title;
    document.getElementById('confirmModalBody').innerHTML = body;
    var actionBtn = document.getElementById('confirmModalAction');
    actionBtn.textContent = actionText;

    // Remove old event listeners and add new one
    var newActionBtn = actionBtn.cloneNode(true);
    actionBtn.parentNode.replaceChild(newActionBtn, actionBtn);
    newActionBtn.addEventListener('click', actionCallback);

    var modalEl = document.getElementById('confirmModal');
    var confirmModal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
    confirmModal.show();
}

function changeMemberRole(memberId, newRole, selectElement) {
    window.api('/api/team/members/' + memberId + '/role', {
        method: 'PUT',
        body: { role: newRole },
        notifyError: false
    })
    .then(function (data) {
        if (data.success) {
            selectElement.setAttribute('data-current-role', newRole);
            showSuccessToast('Role updated to ' + newRole);
            setTimeout(function () { location.reload(); }, 1500);
        } else {
            alert('Failed to change role: ' + (data.error || data.message || 'Unknown error'));
            selectElement.value = selectElement.getAttribute('data-current-role');
        }
    })
    .catch(function (error) {
        console.error('Error:', error);
        alert('An error occurred while changing the role');
        selectElement.value = selectElement.getAttribute('data-current-role');
    })
    .finally(function () {
        bootstrap.Modal.getInstance(document.getElementById('confirmModal')).hide();
    });
}

function suspendMember(memberId, memberName) {
    showConfirmModal(
        'Suspend Team Member',
        'Are you sure you want to suspend <strong>' + memberName + '</strong>? They will lose access to the business until reactivated.',
        'Suspend Member',
        function () { updateMemberStatus(memberId, 'suspended'); }
    );
}

function activateMember(memberId, memberName) {
    // If the plan is already full, there is no seat to activate into — show the
    // capacity modal straight away instead of a confirm dialog that would just
    // be rejected by the server gate.
    var capEl = document.getElementById('capacityModal');
    if (capEl && capEl.dataset.atCap === '1') {
        bootstrap.Modal.getOrCreateInstance(capEl).show();
        return;
    }
    showConfirmModal(
        'Activate Team Member',
        'Are you sure you want to activate <strong>' + memberName + '</strong>? They will regain access to the business.',
        'Activate Member',
        function () { updateMemberStatus(memberId, 'active'); }
    );
}

function removeMember(memberId, memberName) {
    showConfirmModal(
        'Remove Team Member',
        'Are you sure you want to remove <strong>' + memberName + '</strong> from the team? This action cannot be undone.',
        'Remove Member',
        function () { updateMemberStatus(memberId, 'removed'); }
    );
}

function updateMemberStatus(memberId, status) {
    window.api('/api/team/members/' + memberId + '/status', {
        method: 'PUT',
        body: { status: status },
        notifyError: false,
        throwOnError: false
    })
    .then(function (data) {
        if (data.success) {
            location.reload();
        } else if (data.upgrade_url) {
            // Seat-cap block (422): show the capacity modal with the reason and
            // an upgrade CTA instead of a generic alert.
            showCapacityModal(data);
        } else {
            alert('Failed to update member status: ' + (data.error || data.message || 'Unknown error'));
        }
    })
    .catch(function (error) {
        console.error('Error:', error);
        alert('An error occurred while updating member status');
    })
    .finally(function () {
        bootstrap.Modal.getInstance(document.getElementById('confirmModal')).hide();
    });
}

// Seat-cap block when activating a member. The confirm modal is still closing,
// so wait for it to fully hide before showing the capacity modal — avoids a
// stacked backdrop. Falls back to immediate show if no confirm modal is open.
function showCapacityModal(data) {
    var confirmEl = document.getElementById('confirmModal');
    var capEl = document.getElementById('capacityModal');
    if (!capEl) {
        alert(data.error || 'You have reached your plan user limit.');
        return;
    }
    document.getElementById('capacityModalBody').textContent =
        data.error || 'You have reached your plan user limit.';
    // The upgrade anchor is absent in the native shells (App Store 3.1.1 gate).
    var upgradeBtn = document.getElementById('capacityUpgradeBtn');
    if (data.upgrade_url && upgradeBtn) {
        upgradeBtn.href = data.upgrade_url;
    }

    var show = function () {
        bootstrap.Modal.getOrCreateInstance(capEl).show();
    };
    var confirmInstance = confirmEl && bootstrap.Modal.getInstance(confirmEl);
    if (confirmInstance) {
        confirmEl.addEventListener('hidden.bs.modal', show, { once: true });
        confirmInstance.hide();
    } else {
        show();
    }
}

function resendInvitation(invitationId, email) {
    window.api('/api/team/invitations/' + invitationId + '/resend', {
        method: 'POST',
        notifyError: false
    })
    .then(function (data) {
        if (data.success) {
            alert('Invitation resent to ' + email);
            location.reload();
        } else {
            alert('Failed to resend invitation: ' + (data.message || 'Unknown error'));
        }
    })
    .catch(function (error) {
        console.error('Error:', error);
        alert('An error occurred while resending invitation');
    });
}

function cancelInvitation(invitationId, email) {
    showConfirmModal(
        'Cancel Invitation',
        'Are you sure you want to cancel the invitation to <strong>' + email + '</strong>?',
        'Cancel Invitation',
        function () {
            window.api('/api/team/invitations/' + invitationId + '/cancel', {
                method: 'POST',
                notifyError: false
            })
            .then(function (data) {
                if (data.success) {
                    location.reload();
                } else {
                    alert('Failed to cancel invitation: ' + (data.message || 'Unknown error'));
                }
            })
            .catch(function (error) {
                console.error('Error:', error);
                alert('An error occurred while canceling invitation');
            })
            .finally(function () {
                bootstrap.Modal.getInstance(document.getElementById('confirmModal')).hide();
            });
        }
    );
}

// Expose for template dispatcher (data-action="call-fn" in global-actions.js).
window.activateMember = activateMember;
window.cancelInvitation = cancelInvitation;
window.removeMember = removeMember;
window.resendInvitation = resendInvitation;
window.suspendMember = suspendMember;
